import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { useFilters } from "@/components/filters";
import { ListPage } from "@/components/list-page";
import { Dialog, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import { getAllCajas } from "@/features/cajas/caja-list/api/get-all-cajas";
import { getCaja } from "@/features/cajas/caja-list/api/get-caja";
import { getAllCajaAperturas, useGetAllCajaAperturasQuery } from "@/features/cajas/caja-historial/api/get-all-caja-aperturas";
import { GetCajaAperturaQuery } from "@/features/cajas/caja-historial/api/get-caja-apertura";
import ViewCajaApertura from "@/features/cajas/caja-historial/components/view-caja-apertura";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { formatDate, formatString } from "@/shared/formats";
import { CajaApertura } from "@/shared/types/caja-apertura";

export default function HistorialCajaAperturasPage() {
    const openModal = useOpenModal();

    const filterData = useFilters({
        filters: [
            { type: "dateRange", label: "Fecha de Apertura", fromName: "aperturaDesde", toName: "aperturaHasta", fromLabel: "Apertura Desde", toLabel: "Apertura Hasta" },
            { type: "dateRange", label: "Fecha de Cierre", fromName: "cierreDesde", toName: "cierreHasta", fromLabel: "Cierre Desde", toLabel: "Cierre Hasta" },
            { type: "model", label: "Caja", name: "cajaId", get: getCaja, getAll: getAllCajas },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllCajaAperturasQuery(filterData.filterData);

    const columnData = useColumns<CajaApertura>({
        key: "caja_aperturas",
        columns: [
            { key: "fechaApertura", header: "Apertura", render: row => formatDate(row.fechaApertura, true), width: "auto" },
            { key: "fechaCierre", header: "Cierre", render: row => formatDate(row.fechaCierre, true), width: "auto" },
            { key: "caja", header: "Caja", render: row => formatString(row.cajaNombre) },
            { key: "abiertoPor", header: "Abierto Por", render: row => formatString(row.abiertoPor) },
            { key: "cerradoPor", header: "Cerrado Por", render: row => formatString(row.cerradoPor) },
            { key: "estado", header: "Estado", render: row => row.abierto ? "Abierto" : "Cerrado", width: "auto", align: "center" },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ]
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Historial de Arqueo de Caja">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>

        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.ViewButton onClick={() => openModal("view", row.id)} />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Dialog code="view" size="md">
            <ModalHeader>Ver Arqueo de Caja</ModalHeader>
            <ModalValueExtractor>
                {cajaAperturaId => <GetCajaAperturaQuery id={cajaAperturaId}>
                    {query => <DataFetcher query={query}>
                        {cajaApertura => <ViewCajaApertura
                            cajaApertura={cajaApertura}
                            padding={3}
                        />}
                    </DataFetcher>}
                </GetCajaAperturaQuery>}
            </ModalValueExtractor>
        </Dialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCajaAperturas}
            defaultFileTitle="Historial de Aperturas"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCajaAperturas}
            defaultFileTitle="Historial de Aperturas"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCajaAperturas}
            defaultFileTitle="Historial de Aperturas"
        />
    </ListPage>
}